import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import TableLayout from '../global/Table/TableLayout';
import { WebhooksTableColumns } from './Table/WebhooksTableColumns';
import WebhooksTableRow from './Table/WebhooksTableRow';

import useTableActions from '../../hooks/common/useTableActions';
import { useGetWebhooks } from '../../hooks/webhooks/useGetWebhooks';
import { useDeleteWebhook } from '../../hooks/webhooks/useDeleteWebhook';
import { useUpdateWebhook } from '../../hooks/webhooks/useUpdateWebhook';
import { usePermissions } from '../../auth/PermissionsContext';

const WebhooksTable = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const { has } = usePermissions();
    const navigate = useNavigate();

    const perms = useMemo(
        () => ({
            list: has('webhooks.manage'),
            create: has('webhooks.manage'),
            edit: has('webhooks.manage'),
            delete: has('webhooks.manage'),
        }),
        [has]
    );

    const [activeFilter, setActiveFilter] = useState('');

    const { deleteWebhookMutate, isDeleting } = useDeleteWebhook();
    const { updateWebhookMutate, isUpdating } = useUpdateWebhook();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleView,
        handleEdit,
        handleMenuDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: '/settings/integrations/webhooks',
        deleteSingle: deleteWebhookMutate,
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            is_active: activeFilter === '' ? undefined : activeFilter === 'true',
        }),
        [page, perPage, search, activeFilter]
    );

    const { webhooks, meta, isLoading, error, refetch } = useGetWebhooks(params);
    console.log("🚀 ~ WebhooksTable ~ webhooks:", webhooks)

    // Optimistic-ish toggle. The hook invalidates the list query on success
    // which produces the authoritative state from the server.
    const handleToggleActive = (webhook, checked) => {
        const events = Array.isArray(webhook.events) ? webhook.events : [];
        updateWebhookMutate({
            id: webhook.id,
            data: {
                name: webhook.name,
                url: webhook.url,
                events,
                is_active: checked,
            },
        });
    };

    const tableFilters = [
        {
            name: 'is_active',
            label: { ar: 'الحالة', en: 'Status' },
            value: activeFilter,
            onChange: (val) => setActiveFilter(val),
            options: [
                { value: '',      label: { ar: 'الكل',     en: 'All' } },
                { value: 'true',  label: { ar: 'مُفعّل',   en: 'Active' } },
                { value: 'false', label: { ar: 'متوقف',   en: 'Inactive' } },
            ],
        },
    ];

    const renderRow = (rowParams) => (
        <WebhooksTableRow
            key={rowParams.item.id}
            webhook={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            onToggleActive={handleToggleActive}
            isToggling={isUpdating}
        />
    );

    return (
        <TableLayout
            title={isArabic ? 'الـ Webhooks' : 'Webhooks'}
            subtitle={isArabic
                ? 'اشتراكات الأحداث الخارجية — تستقبل أحداث المتجر عبر HTTP بتوقيع HMAC.'
                : 'Outbound subscriptions — receive shop events as signed HTTP POSTs.'}
            search={search}
            onSearchChange={setSearch}
            addButtonLabel={isArabic ? 'إضافة Webhook' : 'Add Webhook'}
            onAddClick={() => navigate('/settings/integrations/webhooks/new')}
            addButtonVisible={perms.create}
            isLoading={isLoading}
            error={error}
            items={webhooks}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            columns={WebhooksTableColumns}
            renderRow={renderRow}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuView={handleView}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            menuDisableEdit={!perms.edit}
            menuDisableDelete={!perms.delete}
            isDeletingSingle={isDeleting}
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, webhooks)}
            onRowCheckboxClick={handleRowCheckboxClick}
            bulkDeleteDisabled
            filters={tableFilters}
            onClearAllFilters={() => setActiveFilter('')}
            emptyLabelAr="لا توجد اشتراكات Webhook بعد"
            emptyLabelEn="No webhook subscriptions yet"
            refetch={refetch}
        />
    );
};

export default WebhooksTable;
